import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import type { CaseApiModel } from '@visiba/api-vcs-client-v2';
import { Injectable, Injector } from '@visiba/instantiation';
import { EventEmitter } from '@visiba/std';
import { CaseCommunicator } from './_case_communicator';
import { ListedData } from './case.service';
import { AppointmentCaseModel } from './case_appointment_model';
import { MessagingCaseModel } from './case_messaging_model';
import { APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX } from './common';

@Injectable()
export class CaseCacheService {
	public readonly signaler = new EventEmitter<{
		onCaseListUpdate: void;
	}>();

	private appointmentCases = new Map<string, AppointmentCaseModel>();
	private messagingCases = new Map<number, MessagingCaseModel>();

	constructor(
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly caseCommunicator: CaseCommunicator,
	) {
		this.caseCommunicator.on('removeAppointment', (id) => {
			const $case = this.appointmentCases.get(id);
			if ($case != null) {
				this.removeCase($case);
			}
		});

		this.caseCommunicator.on('removeMessagingCase', (id) => {
			const $case = this.messagingCases.get(id);
			if ($case != null) {
				this.removeCase($case);
				this.signaler.emit('onCaseListUpdate', void 0);
			}
		});
	}

	/**
	 * We would want to remove this in favour of something a lot simpler.
	 *
	 * @deprecated
	 * */
	public getGroupedCases(): {
		completed: {
			appointmentCases: AppointmentCaseModel[];
			messagingCases: MessagingCaseModel[];
		};
		ongoing: {
			appointmentCases: AppointmentCaseModel[];
			messagingCases: MessagingCaseModel[];
		};
	} {
		const completedAppointmentCases: AppointmentCaseModel[] = [];
		const completedMessagingCases: MessagingCaseModel[] = [];
		const ongoingAppointmentCases: AppointmentCaseModel[] = [];
		const ongoingMessagingCases: MessagingCaseModel[] = [];

		for (const appointmentCase of this.appointmentCases.values()) {
			const data = appointmentCase.get();

			if (data.completed) {
				completedAppointmentCases.push(appointmentCase);
			} else {
				ongoingAppointmentCases.push(appointmentCase);
			}
		}

		for (const messagingCase of this.messagingCases.values()) {
			const data = messagingCase.get();

			if (data.completed && data.issues.unreadMessages === 0) {
				completedMessagingCases.push(messagingCase);
			} else {
				ongoingMessagingCases.push(messagingCase);
			}
		}

		return {
			completed: {
				appointmentCases: completedAppointmentCases,
				messagingCases: completedMessagingCases,
			},
			ongoing: {
				appointmentCases: ongoingAppointmentCases,
				messagingCases: ongoingMessagingCases,
			},
		};
	}

	/**
	 * We would want to remove this in favour of something a lot simpler.
	 *
	 * @deprecated
	 * */
	public getListedData(): ListedData {
		const appointments = Array.from(this.appointmentCases.values());
		const messaging = Array.from(this.messagingCases.values());

		const listedData: ListedData = {
			appointmentCases: appointments,
			messagingCases: messaging,
		};

		return listedData;
	}

	public removeCase($case: MessagingCaseModel | AppointmentCaseModel): void {
		if ($case instanceof MessagingCaseModel) {
			this.messagingCases.delete(parseInt($case.getIdentifier(), 10));

			return;
		}

		this.appointmentCases.delete($case.getIdentifier());

		this.signaler.emit('onCaseListUpdate', void 0);
	}

	private createMessagingCase(id: number): MessagingCaseModel {
		const caseModel = this.injector.resolve(MessagingCaseModel);

		this.messagingCases.set(id, caseModel);

		return caseModel;
	}

	/**
	 * We would want to remove this in favour of something a lot simpler.
	 *
	 * @deprecated
	 * */
	public updateMessagingCase(caseData: Readonly<CaseApiModel>): MessagingCaseModel {
		if (caseData.MessageConversationId == null) throw new Error('did not expect "MessageConversationId" to be null');

		let caseModel = this.messagingCases.get(caseData.MessageConversationId);
		if (caseModel == null) {
			caseModel = this.createMessagingCase(caseData.MessageConversationId);
		}

		caseModel.updateLesserData(caseData);

		const unhandledIssuesTrackingId =
			MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + caseData.MessageConversationId.toString();
		this.parseUnhandledIssues(unhandledIssuesTrackingId, caseModel);

		return caseModel;
	}

	private createAppointmentCase(id: string): AppointmentCaseModel {
		const caseModel = this.injector.resolve(AppointmentCaseModel);

		this.appointmentCases.set(id, caseModel);

		return caseModel;
	}

	/**
	 * We would want to remove this in favour of something a lot simpler.
	 *
	 * @deprecated
	 * */
	public updateAppointmentCase(caseData: Readonly<CaseApiModel>): AppointmentCaseModel {
		if (caseData.AppointmentTicket == null) throw new Error('did not expect "AppointmentTicket" to be null');

		let caseModel = this.appointmentCases.get(caseData.AppointmentTicket);
		if (caseModel == null) {
			caseModel = this.createAppointmentCase(caseData.AppointmentTicket);
		}

		caseModel.updateLesserData(caseData);

		const unhandledIssuesTrackingId = APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + caseData.AppointmentTicket;
		this.parseUnhandledIssues(unhandledIssuesTrackingId, caseModel);

		return caseModel;
	}

	// TODO: This should be private but we need to expose it for OPW
	public parseUnhandledIssues(trackingId: string, caseModel: AppointmentCaseModel | MessagingCaseModel): void {
		this.unhandledIssuesService.removeIssuesByTrackingId(trackingId);
		const { issues, completed } = caseModel.get();
		Array.from(Array(issues.awaitFormResponses).keys()).forEach(() => {
			this.unhandledIssuesService.addIssue(trackingId, [completed ? 'completed' : 'ongoing', 'awaitFormResponses']);
		});
		Array.from(Array(issues.awaitPayment).keys()).forEach(() => {
			this.unhandledIssuesService.addIssue(trackingId, [completed ? 'completed' : 'ongoing', 'awaitPayment']);
		});
		Array.from(Array(issues.isNewCase ? 1 : 0).keys()).forEach(() => {
			this.unhandledIssuesService.addIssue(trackingId, [completed ? 'completed' : 'ongoing', 'isNewCase']);
		});
		Array.from(Array(issues.unreadMessages).keys()).forEach(() => {
			this.unhandledIssuesService.addIssue(trackingId, [completed ? 'completed' : 'ongoing', 'unreadMessages']);
		});
	}
}
